import { useTranslation } from "react-i18next";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";
import UnitAvatar from "../../../components/base/UnitAvatar";
import type { Unit } from "../../../store/admin/unitsStore";
import { formatSpeed, formatUpdatedAgo } from "../services/formatWatchTime";
import {
  AI_MESSAGE_ICONS,
  AI_MESSAGE_TONES,
  CHIP_TONE_CLASSES,
  WATCH_STATUS_CARD,
  WATCH_STATUS_TONES,
} from "../theme";
import type { UnitWatchState } from "../types";

export interface UnitWatchCardProps {
  unit: Unit;
  watch: UnitWatchState;
  onOpen: () => void;
  onCall: () => void;
  onCustody: () => void;
  /** Llevar al caso abierto de esta unidad, listo para cerrarlo. */
  onResolverCaso?: (caseId: string) => void;
}

export default function UnitWatchCard({
  unit,
  watch,
  onOpen,
  onCall,
  onCustody,
  onResolverCaso,
}: UnitWatchCardProps) {
  const { t } = useTranslation("common");

  const style = WATCH_STATUS_CARD[watch.status];
  const statusTone = WATCH_STATUS_TONES[watch.status];
  const messageTone = AI_MESSAGE_TONES[watch.aiMessageType];
  const hasMessage = watch.aiMessage.trim().length > 0;

  const updatedLabel = formatUpdatedAgo(watch.updatedAt, t);
  const speedLabel = formatSpeed(watch.speedKph, t);

  return (
    <div
      className={`overflow-hidden rounded-2xl border bg-card transition-shadow hover:shadow-custom ${style.border}`}
    >
      <div className={`flex items-center gap-3 border-b border-line-soft px-4 pb-3 pt-4 ${style.header}`}>
        <UnitAvatar
          iconId={unit.icon_id}
          icon={unit.icon}
          name={unit.name}
          toneClassName={style.icon}
          fallback={<i className="ri-truck-line text-base" />}
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold leading-tight text-content">{unit.name}</p>
          <p className="mt-0.5 text-[11px] text-content-faint">
            {unit.plate ?? t("monitoreoIa.units.noPlate")}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2 max-sm:flex-col max-sm:items-end max-sm:gap-1.5">
          {watch.isCalling && (
            <span className="inline-flex items-center gap-1 rounded-full border border-info/30 bg-info/10 px-2 py-0.5 text-[10px] font-semibold text-info">
              <i className="ri-phone-line animate-pulse text-[10px]" />
              {t("monitoreoIa.units.onCall")}
            </span>
          )}
          <span className={`h-2 w-2 rounded-full ${style.dot}`} />
          <span
            className={`rounded-full border px-2.5 py-0.5 text-[10.5px] font-semibold ${CHIP_TONE_CLASSES[statusTone]}`}
          >
            {t(`monitoreoIa.status.${watch.status}`)}
          </span>
        </div>
      </div>

      <dl className="space-y-2 border-b border-line-soft px-4 py-3 text-xs">
        <div className="flex items-center justify-between gap-2">
          <dt className="text-content-secondary">{t("monitoreoIa.units.driver")}</dt>
          <dd className="ml-2 max-w-[180px] truncate font-medium text-content max-sm:min-w-0 max-sm:max-w-none">
            {unit.driver?.name ?? t("monitoreoIa.units.noDriver")}
          </dd>
        </div>

        <div className="flex items-center justify-between gap-2">
          <dt className="text-content-secondary">{t("monitoreoIa.units.location")}</dt>
          <dd className="ml-2 max-w-[180px] truncate text-right text-content-secondary max-sm:min-w-0 max-sm:max-w-none">
            {watch.address ?? unit.lastAddress ?? t("monitoreoIa.units.noLocation")}
          </dd>
        </div>

        <div className="flex items-center justify-between gap-2">
          <dt className="shrink-0 text-content-secondary">{t("monitoreoIa.units.state")}</dt>
          <dd className="ml-2 flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${style.dot}`} />
            <span
              className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${CHIP_TONE_CLASSES[statusTone]}`}
            >
              {t(`monitoreoIa.status.${watch.status}`)}
            </span>
          </dd>
        </div>

        <div className="flex items-center justify-between gap-2">
          <dt className="text-content-secondary">{t("monitoreoIa.units.speed")}</dt>
          <dd className="font-medium text-content">{speedLabel}</dd>
        </div>

        <div className="flex items-center justify-between gap-2">
          <dt className="text-content-secondary">{t("monitoreoIa.units.updated")}</dt>
          <dd className="text-content-faint">{updatedLabel}</dd>
        </div>
      </dl>

      <div className="px-4 py-3">
        <div
          className={`rounded-xl border p-3 ${
            hasMessage && messageTone !== "neutral"
              ? CHIP_TONE_CLASSES[messageTone]
              : "border-line bg-subtle"
          }`}
        >
          <div className="flex items-start gap-2">
            <CentinelaAvatar size="xs" className="mt-0.5 shrink-0" />
            <p
              className={`text-[11.5px] leading-relaxed ${
                hasMessage ? "" : "italic text-content-faint"
              }`}
            >
              {hasMessage && messageTone !== "neutral" && (
                <i
                  className={`${AI_MESSAGE_ICONS[watch.aiMessageType]} mr-1 align-[-0.1em] text-xs`}
                />
              )}
              {hasMessage ? watch.aiMessage : t("monitoreoIa.units.noAiMessage")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 px-4 pb-4">
        <button
          type="button"
          onClick={onCall}
          className="flex min-w-[72px] flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-xl bg-primary-500 px-2 py-2.5 text-[11.5px] max-sm:whitespace-normal max-sm:text-xs font-bold text-on-primary transition-colors hover:bg-primary-400"
        >
          <i className="ri-phone-line text-[11px]" />
          {t("monitoreoIa.units.call")}
        </button>

        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-[100px] flex-[2] items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-info px-3 py-2.5 text-[11.5px] max-sm:whitespace-normal max-sm:text-xs font-bold text-white transition-opacity hover:opacity-90"
        >
          <i className="ri-dashboard-3-line text-[11px]" />
          {t("monitoreoIa.units.openDetail")}
        </button>

        {/* ── Junto a Detalle ─────────────────────────────────────────────
            Abre los casos abiertos de la flota con el de ESTA unidad arriba.
            Va aquí y no aparte porque es la misma clase de acción que las
            otras tres —algo que haces con la unidad desde el listado— y en su
            propio color porque es la única que pide una decisión.

            Sólo si hay caso: un botón deshabilitado permanente enseña a
            ignorar esa esquina de la tarjeta. */}
        {watch.openCaseId && onResolverCaso ? (
          <button
            type="button"
            onClick={() => onResolverCaso(watch.openCaseId as string)}
            className="flex min-w-[88px] flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-xl border border-warning/40 bg-warning/10 px-2 py-2.5 text-[11.5px] max-sm:whitespace-normal max-sm:text-xs font-bold text-warning transition-colors hover:bg-warning/20"
          >
            <i className="ri-check-double-line text-[11px]" />
            {t("monitoreoIa.units.resolverCaso", { defaultValue: "Resolver" })}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onCustody}
          className="flex min-w-[72px] flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-xl border border-info/30 bg-info/10 px-2 py-2.5 text-[11.5px] max-sm:whitespace-normal max-sm:text-xs font-semibold text-info transition-colors hover:bg-info/20"
        >
          <i className="ri-shield-check-line text-[11px]" />
          {t("monitoreoIa.units.custody")}
        </button>
      </div>
    </div>
  );
}
